import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';

/**
 * Switches the active store.
 *
 * The list comes from the server's grant check, so a store the user cannot see
 * is never sent rather than merely hidden here. Switching clears the query cache
 * — see AuthContext.selectStore.
 */
export function StoreSwitcher(): React.JSX.Element | null {
  const { stores, activeStore, selectStore } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent): void {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!activeStore) return null;

  // Nothing to switch between: show the name, but not a control that implies
  // there is a choice.
  if (stores.length === 1) {
    return (
      <span className="rounded border border-rule px-2 py-1 text-xs text-ink-muted">
        {activeStore.displayName}
      </span>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded border border-rule px-2 py-1 text-xs text-ink-muted hover:border-rule-strong hover:text-ink"
      >
        {activeStore.displayName}
        <ChevronDown size={13} aria-hidden />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Select store"
          className="qs-panel absolute top-full left-0 z-30 mt-1 max-h-72 min-w-56 overflow-auto py-1 shadow-sm"
        >
          {stores.map((store) => {
            const isActive = store.storeId === activeStore.storeId;

            return (
              <li key={store.storeId} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => {
                    selectStore(store.storeId);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs',
                    isActive ? 'text-ink' : 'text-ink-muted hover:bg-sunken hover:text-ink',
                  )}
                >
                  <Check
                    size={13}
                    aria-hidden
                    className={cn('shrink-0', isActive ? 'text-accent' : 'invisible')}
                  />
                  <span className="min-w-0 flex-1 truncate">{store.displayName}</span>
                  <span className="qs-eyebrow shrink-0">{store.role}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
